import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Unlink, Settings } from "lucide-react";
import api from "../lib/api";

const PLATFORMS = [
  {
    id: "youtube", provider: "google", name: "YouTube", icon: "▶", desc: "Post videos, shorts, and community posts",
    color: "from-red-600 to-red-700",
  },
  {
    id: "tiktok", provider: "tiktok", name: "TikTok", icon: "♪", desc: "Share short videos and clips",
    color: "from-gray-900 to-gray-800",
  },
  {
    id: "instagram", provider: "meta", name: "Instagram", icon: "📷", desc: "Post photos, reels, and stories",
    color: "from-purple-600 via-pink-500 to-orange-400",
  },
  {
    id: "x", provider: "x", name: "X (Twitter)", icon: "𝕏", desc: "Post tweets and threads",
    color: "from-gray-900 to-black",
  },
  {
    id: "microsoft", provider: "microsoft", name: "Microsoft", icon: "⊞", desc: "Microsoft account linking",
    color: "from-blue-600 to-blue-700",
  },
];

export default function SocialAccountsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [providerStatus, setProviderStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectModal, setConnectModal] = useState(null);
  const [username, setUsername] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/social-accounts").then((r) => setAccounts(r.data)),
      api.get("/auth/providers/status").then((r) => setProviderStatus(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    // Check for callback params
    const connected = searchParams.get("connected");
    if (connected) toast.success(`Connected to ${connected}!`);
  }, [searchParams]);

  const getAccount = (platform) => accounts.find((a) => a.platform === platform && a.is_connected);

  const handleConnect = async (p) => {
    // Google/YouTube uses Emergent's managed OAuth bridge
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (p.provider === "google") {
      setConnecting(true);
      const redirectUrl = window.location.origin + '/settings/social-accounts';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    const isConfigured = providerStatus[p.provider]?.configured;
    if (isConfigured) {
      // Real OAuth flow
      setConnecting(true);
      try {
        const r = await api.get(`/auth/${p.provider}/connect`);
        window.location.href = r.data.auth_url;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Connection failed");
        setConnecting(false);
      }
    } else {
      // Mocked flow
      setConnectModal(p);
    }
  };

  const handleMockedConnect = async () => {
    if (!username.trim() || !connectModal) return;
    setConnecting(true);
    try {
      await api.post("/social-accounts/connect", { platform: connectModal.id, platform_username: username.trim() });
      setAccounts((prev) => {
        const filtered = prev.filter((a) => a.platform !== connectModal.id);
        return [...filtered, { platform: connectModal.id, platform_username: username.trim(), platform_display_name: username.trim(), is_connected: true, connected_at: new Date().toISOString(), post_count: 0 }];
      });
      toast.success(`Connected to ${connectModal.name}!`);
      setConnectModal(null);
      setUsername("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (platform, provider) => {
    setDisconnecting(platform);
    try {
      const isConfigured = providerStatus[provider]?.configured;
      if (isConfigured) {
        await api.delete(`/auth/${provider}/disconnect`);
      } else {
        await api.delete(`/social-accounts/${platform}`);
      }
      setAccounts((prev) => prev.map((a) => a.platform === platform ? { ...a, is_connected: false } : a));
      toast.success("Disconnected");
    } catch {
      toast.error("Failed to disconnect");
    } finally {
      setDisconnecting("");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  return (
    <div className="min-h-screen" data-testid="social-accounts-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="font-outfit text-sm font-semibold text-foreground">Connected Accounts</h1>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="font-outfit text-2xl font-semibold text-foreground mb-2">Social Media Accounts</h2>
          <p className="text-sm text-muted-foreground">Connect your accounts to let AI companions publish content across platforms.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="platform-cards">
          {PLATFORMS.map((p) => {
            const acct = getAccount(p.id);
            const isConfigured = providerStatus[p.provider]?.configured;
            return (
              <div key={p.id} className="bg-card rounded-2xl border border-border/60 overflow-hidden shadow-sm" data-testid={`platform-card-${p.id}`}>
                <div className={`bg-gradient-to-r ${p.color} h-20 flex items-center justify-center`}>
                  <span className="text-4xl text-white drop-shadow-lg">{p.icon}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-outfit text-lg font-semibold text-foreground">{p.name}</h3>
                    {!isConfigured && !acct && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium flex items-center gap-1" data-testid={`config-badge-${p.id}`}>
                        <Settings className="w-3 h-3" /> Demo Mode
                      </span>
                    )}
                  </div>
                  {acct ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm text-emerald-400 font-medium">Connected</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-1">@{acct.platform_username}</p>
                      <p className="text-xs text-muted-foreground/70 mb-3">{acct.post_count} posts &middot; Connected {new Date(acct.connected_at).toLocaleDateString()}</p>
                      <Button
                        onClick={() => handleDisconnect(p.id, p.provider)}
                        disabled={disconnecting === p.id}
                        variant="outline"
                        className="w-full border-red-500/30 text-red-500 hover:bg-red-500/10 h-9 text-xs"
                        data-testid={`disconnect-${p.id}`}
                      >
                        {disconnecting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Unlink className="w-3.5 h-3.5 mr-1" /> Disconnect</>}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mb-4">{p.desc}</p>
                      <Button
                        onClick={() => handleConnect(p)}
                        disabled={connecting}
                        className={`w-full bg-gradient-to-r ${p.color} text-white h-10 text-sm rounded-lg press-down`}
                        data-testid={`connect-${p.id}`}
                      >
                        {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Connect ${p.name}`}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Connect Modal */}
      {connectModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setConnectModal(null); setUsername(""); }}>
          <div className="bg-card rounded-2xl shadow-xl max-w-sm w-full overflow-hidden animate-fade-in-up" onClick={(e) => e.stopPropagation()} data-testid="connect-modal">
            <div className={`bg-gradient-to-r ${connectModal.color} px-6 py-5`}>
              <h2 className="font-outfit text-lg font-semibold text-white">Connect to {connectModal.name}</h2>
              <p className="text-xs text-white/70 mt-1">Authorize App-Mi.ai to post on your behalf</p>
            </div>
            <div className="p-6">
              <label className="block text-xs font-medium text-foreground/80 mb-2">Your {connectModal.name} username</label>
              <input
                data-testid="connect-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={`@your${connectModal.id}handle`}
                className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 mb-4"
                autoFocus
              />
              <Button
                onClick={handleMockedConnect}
                disabled={connecting || !username.trim()}
                className={`w-full bg-gradient-to-r ${connectModal.color} text-white h-11 text-sm rounded-lg press-down disabled:opacity-40`}
                data-testid="authorize-btn"
              >
                {connecting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Authorizing...</> : "Authorize"}
              </Button>
              <button onClick={() => { setConnectModal(null); setUsername(""); }} className="w-full mt-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer hover:text-foreground/80">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
